import { useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatCedi } from "../lib/api";
import { revenueByMonth, leadFunnel, departmentPerformance, topCustomers } from "../lib/mockData";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
  BarChart, Bar, RadialBarChart, RadialBar, Legend,
} from "recharts";
import { Download, FileSpreadsheet, FileText, Filter, TrendingUp, Users, ShoppingCart, DollarSign, Target, Award } from "lucide-react";
import { toast } from "sonner";
import BeforeAfter from "../components/common/BeforeAfter";
import ConsultationCTA from "../components/common/ConsultationCTA";

const DemoHeader = () => (
  <div className="mb-8">
    <div className="text-xs font-semibold uppercase tracking-widest text-primary">Demo 7</div>
    <h1 className="mt-1 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">Executive Analytics Dashboard</h1>
    <p className="mt-3 text-muted-foreground text-lg max-w-3xl">
      Every KPI that matters, in one live view. Filter by date, switch roles, and export polished reports.
    </p>
  </div>
);

const roles = [
  { id: "ceo", label: "CEO" },
  { id: "cfo", label: "CFO" },
  { id: "cmo", label: "CMO" },
  { id: "ops", label: "Head of Ops" },
];

const KPI = ({ icon: Icon, label, value, delta, positive = true }) => (
  <Card className="glass"><CardContent className="p-5">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="w-4 h-4"/></div>
    </div>
    <div className="mt-2 font-display text-2xl font-bold">{value}</div>
    <div className={`text-xs mt-1 ${positive ? "text-accent" : "text-destructive"}`}>{positive ? "▲" : "▼"} {delta}</div>
  </CardContent></Card>
);

const download = (name, content) => {
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

export default function Demo7Analytics() {
  const [role, setRole] = useState("ceo");
  const [range, setRange] = useState("6m");

  const exportExcel = () => {
    const csv = "Month,Revenue,Orders\n" + revenueByMonth.map((r) => `${r.month},${r.revenue},${r.orders}`).join("\n");
    download("stafflink-report.csv", csv);
    toast.success("Excel-friendly CSV downloaded");
  };
  const exportPDF = () => {
    window.print();
    toast.success("Use your browser's Save-as-PDF option");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <DemoHeader/>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-40 h-9" data-testid="analytics-role"><SelectValue/></SelectTrigger>
          <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.label} view</SelectItem>)}</SelectContent>
        </Select>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-32 h-9" data-testid="analytics-range"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="1m">Last 30 days</SelectItem>
            <SelectItem value="3m">Last 3 months</SelectItem>
            <SelectItem value="6m">Last 6 months</SelectItem>
            <SelectItem value="1y">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="gap-1"><Filter className="w-3 h-3"/> {roles.find((r) => r.id === role)?.label} · {range.toUpperCase()}</Badge>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-excel"><FileSpreadsheet className="w-4 h-4 mr-1"/> Excel</Button>
          <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-pdf"><FileText className="w-4 h-4 mr-1"/> PDF</Button>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
        <KPI icon={DollarSign} label="Revenue" value={formatCedi(84900)} delta="18.6% vs Dec"/>
        <KPI icon={ShoppingCart} label="Sales" value="441" delta="8.4%"/>
        <KPI icon={Users} label="Customers" value="1,282" delta="12.1%"/>
        <KPI icon={Target} label="Lead conversion" value="17.3%" delta="2.3%"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="glass lg:col-span-2"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-primary">Revenue vs Orders</div>
              <div className="font-display text-lg font-semibold">Growth trajectory</div>
            </div>
            <Badge variant="outline"><TrendingUp className="w-3 h-3 mr-1"/> Trending up</Badge>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={revenueByMonth}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#005BBB" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#005BBB" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v) => formatCedi(v)}/>
                <Area type="monotone" dataKey="revenue" stroke="#005BBB" strokeWidth={3} fill="url(#gRev)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>

        <Card className="glass"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-primary">Department scorecard</div>
          <div className="font-display text-lg font-semibold mb-2">Performance index</div>
          <div className="h-72">
            <ResponsiveContainer>
              <RadialBarChart innerRadius={20} outerRadius={110} data={departmentPerformance} startAngle={90} endAngle={-270}>
                <RadialBar dataKey="score" cornerRadius={10} background label={{ fill: "hsl(var(--foreground))", fontSize: 11 }} fill="#005BBB"/>
                <Legend iconSize={8} layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: 12 }}/>
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="glass"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-primary">Lead funnel</div>
          <div className="font-display text-lg font-semibold mb-3">Visitors → Closed Won</div>
          <div className="space-y-2">
            {leadFunnel.map((f, i) => {
              const pct = Math.round((f.value / leadFunnel[0].value) * 100);
              return (
                <div key={f.stage} className="rounded-lg bg-muted/50 p-3">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{f.stage}</span>
                    <span className="text-muted-foreground">{f.value.toLocaleString()} · {pct}%</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-background overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent></Card>

        <Card className="glass"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-primary">Top customers</div>
          <div className="font-display text-lg font-semibold mb-3">By revenue</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={topCustomers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <YAxis dataKey="name" type="category" width={110} stroke="hsl(var(--muted-foreground))" fontSize={11}/>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v) => formatCedi(v)}/>
                <Bar dataKey="spent" fill="#00C853" radius={[0, 8, 8, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <KPI icon={Award} label="Support CSAT" value="96%" delta="1.4%"/>
        <KPI icon={Users} label="Employee productivity" value="88" delta="3.1%"/>
        <KPI icon={ShoppingCart} label="Appointments" value="248" delta="14%"/>
        <KPI icon={Download} label="Inventory value" value={formatCedi(74300)} delta="6.2%"/>
      </div>

      <BeforeAfter demoId={7}/>
      <ConsultationCTA demoLabel="Executive Analytics"/>
    </div>
  );
}
